// Helpers extracted from FeedItemReader.tsx — pure utils, the
// `useExtractedContent` hook, and the status-banner factory.

import { useCallback, useEffect, useRef, useState } from "react";
import {
  extractArticleHtml,
  isSummaryFeedItem,
  type ExtractStatus,
  type FeedItem,
} from "@/lib/feedReader";

export const formatFeedDate = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

// Trailing previous/next post navigation in cached extracted_html.
const TRAILING_NAV_CLASS_RE = /\b(?:nav-links|post-navigation|post-nav|prev-next|prevnext|pagination|page-nav|page-navigation|adjacent-posts|article-nav|entry-nav|post-pagination)\b/i;
const TRAILING_PREV_RE = /^\s*(?:[←‹«]\s*)?previous(?:\s+(?:post|article|story|entry))?\b/i;
const TRAILING_NEXT_RE = /^\s*next(?:\s+(?:post|article|story|entry))?\b.*?(?:\s*[→›»])?\s*$/i;
const TRAILING_COMBINED_RE = /(?:[←‹«]\s*)?previous[^\n]{0,80}\bnext\b/i;

export const stripTrailingNav = (html: string): string => {
  if (!html || typeof window === "undefined") return html;
  try {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
    const root = doc.body.firstElementChild;
    if (!root) return html;
    let removed = 0;
    while (removed < 5) {
      const last = root.lastElementChild;
      if (!last) break;
      const tag = last.tagName.toLowerCase();
      const cls = `${last.getAttribute("class") ?? ""} ${last.getAttribute("id") ?? ""}`;
      const text = (last.textContent ?? "").replace(/\s+/g, " ").trim();
      const isNav =
        tag === "nav" ||
        TRAILING_NAV_CLASS_RE.test(cls) ||
        (text.length > 0 && text.length < 300 && TRAILING_COMBINED_RE.test(text)) ||
        (text.length > 0 && text.length < 120 &&
          (TRAILING_PREV_RE.test(text) || TRAILING_NEXT_RE.test(text)) &&
          last.querySelectorAll("a").length >= 1);
      if (!isNav) break;
      last.remove();
      removed += 1;
    }
    return root.innerHTML;
  } catch {
    return html;
  }
};

export type ExtractionState = {
  loading: boolean;
  html: string | null;
  status: ExtractStatus | null;
  /** Bumped to force a retry */
  attempt: number;
};

export type ExtractionCompleteCb = (
  itemId: string,
  patch: { extracted_html: string | null; extraction_status: ExtractStatus },
) => void;

const SAFETY_TIMEOUT_MS = 20_000;

/** On-demand article extraction with cache reuse + safety timeout + retry. */
export const useExtractedContent = (
  item: FeedItem | null,
  onComplete?: ExtractionCompleteCb,
) => {
  const [state, setState] = useState<ExtractionState>({
    loading: false, html: null, status: null, attempt: 0,
  });
  const lastKeyRef = useRef<string | null>(null);
  const itemRef = useRef(item);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    itemRef.current = item;
    onCompleteRef.current = onComplete;
  });

  const itemId = item?.id ?? null;

  const retry = useCallback(() => {
    if (!itemRef.current) return;
    lastKeyRef.current = null;
    setState((s) => ({ ...s, attempt: s.attempt + 1, loading: true, status: null, html: null }));
  }, []);

  useEffect(() => {
    if (!itemId) {
      setState({ loading: false, html: null, status: null, attempt: 0 });
      lastKeyRef.current = null;
      return;
    }
    const key = `${itemId}:${state.attempt}`;
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    type Cached = FeedItem & {
      extracted_html?: string | null;
      extraction_status?: string | null;
    };
    const current = itemRef.current as Cached | null;
    if (!current) return;

    if (state.attempt === 0 && current.extracted_html && current.extraction_status === "ok") {
      setState({ loading: false, html: current.extracted_html, status: "ok", attempt: 0 });
      return;
    }
    if (state.attempt === 0 && !isSummaryFeedItem(current)) {
      setState({ loading: false, html: null, status: null, attempt: 0 });
      return;
    }
    if (state.attempt === 0) {
      const prevStatus = current.extraction_status as ExtractStatus | null | undefined;
      if (prevStatus && prevStatus !== "ok") {
        setState({ loading: false, html: null, status: prevStatus, attempt: 0 });
        return;
      }
    }

    setState((s) => ({ ...s, loading: true, html: null, status: null }));

    const watchdog = window.setTimeout(() => {
      if (lastKeyRef.current !== key) return;
      setState((s) => (s.loading ? { ...s, loading: false, status: "timeout", html: null } : s));
    }, SAFETY_TIMEOUT_MS);

    void extractArticleHtml(itemId).then((res) => {
      window.clearTimeout(watchdog);
      if (lastKeyRef.current !== key) return;
      setState({ loading: false, html: res.html, status: res.status, attempt: state.attempt });
      onCompleteRef.current?.(itemId, { extracted_html: res.html, extraction_status: res.status });
    });
    return () => {
      window.clearTimeout(watchdog);
    };
  }, [itemId, state.attempt]);

  return { state, retry };
};

export type ExtractionBanner = { tone: "muted" | "warn"; message: string; canRetry: boolean };

/** Maps an extraction status to a banner config, or null if no banner. */
export const computeExtractionBanner = (state: ExtractionState): ExtractionBanner | null => {
  if (!state.status || state.status === "ok" || state.loading) return null;
  switch (state.status) {
    case "paywall":
      return { tone: "muted", message: "Preview only — open the original for the full article.", canRetry: false };
    case "skipped_insecure":
      return { tone: "warn", message: "This source uses an insecure connection — open the original to read.", canRetry: false };
    case "blocked_private":
      return { tone: "warn", message: "This article's link points to a private or internal address and was not fetched for security reasons.", canRetry: false };
    case "cap_reached":
      return { tone: "muted", message: "Daily article fetch limit reached — resets at midnight UTC.", canRetry: false };
    case "too_large":
      return { tone: "muted", message: "Article is too large to inline — open the original to read.", canRetry: false };
    case "timeout":
      return { tone: "muted", message: "The article took too long to load. You can try again.", canRetry: true };
    case "failed":
      return { tone: "muted", message: "Couldn't load the full article — showing the feed preview.", canRetry: true };
    default:
      return null;
  }
};
